import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { DialogFocusDirective } from './dialog-focus.directive';
import { IconComponent } from './icon.component';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { UI_REASONS } from './ui-reasons';
import { setOneOf, type UiInspector } from '@sharedlib/sheets-editor/ui-inspector';
import type { SheetsUiValue } from '@sharedlib/common/sheets/ui-commands';
import type { PdfOptions } from '@sharedlib/sheets-editor/workbook-pdf';

/** A sheet the dialog offers, in tab order. */
export interface PdfSheetChoice {
  readonly id: string;
  readonly name: string;
}

/**
 * Export to PDF — Figma frame 13 (`13_PDF_Export_Dialog`), with the prototype's choice of what to export
 * (editor review rows 64 and 68; AM-41 adopts file-formats.md: "Whole workbook or chosen worksheet").
 *
 * "Entire workbook" or "One sheet"; the second shows a "Sheet" select listing every sheet, the active one
 * first chosen. The prototype says "One worksheet" and "Worksheet"; AM-36 calls a tab a sheet (Wook,
 * 2026-10-07). The wording of the explanation is taken from the frame rather than paraphrased,
 * because it is what tells the user which cells will and will not appear. While the PDF is made, both
 * buttons are disabled and the primary reads "Preparing download…"; it is disabled until the engine is
 * ready too. The host closes the dialog when the file is made, and leaves it open if it fails.
 */
@Component({
  selector: 'app-pdf-dialog',
  standalone: true,
  imports: [IconComponent, DialogFocusDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './pdf-dialog.component.html',
  styleUrls: ['./pdf-dialog.component.less', './sheets-buttons.css'],
})
export class PdfDialogComponent implements UiInspector {
  /** NFR-8: the panel's words, from sheets.strings.ts. */
  protected readonly S = SHEETS_STRINGS;
  /** Every sheet, in tab order, for "One sheet". */
  @Input() public set sheets(sheets: readonly PdfSheetChoice[]) {
    this.sheetList.set(sheets);
  }
  protected readonly sheetList = signal<readonly PdfSheetChoice[]>([]);
  /** The sheet "One sheet" starts on: the active one. */
  @Input() public set activeSheetId(id: string | null) {
    this.active.set(id);
  }
  /** False until the engine is ready: nothing can be made before. */
  @Input() public ready = true;
  /** True while the PDF is made. */
  @Input() public busy = false;
  @Output() public readonly confirm = new EventEmitter<PdfOptions>();
  @Output() public readonly cancelled = new EventEmitter<void>();

  protected readonly scope = signal<PdfOptions['scope']>('workbook');
  protected readonly orientation = signal<PdfOptions['orientation']>('landscape');
  protected readonly paper = signal<PdfOptions['paper']>('a4');
  /** The sheet picked in "Sheet"; until one is, the active sheet. */
  protected readonly sheetId = signal<string | null>(null);
  private readonly active = signal<string | null>(null);
  protected readonly chosenSheet = computed(() => this.sheetId() ?? this.active() ?? this.sheetList()[0]?.id ?? null);

  /** Cancel, the X, Escape and the backdrop; not while the PDF is made. */
  protected dismiss(): void {
    if (!this.busy) this.cancelled.emit();
  }

  // ------------------------------------------- sheets_ui (MCP contract, 6 Oct: "set fields … apply/cancel")

  public uiFields(): Readonly<Record<string, unknown>> {
    return {
      scope: this.scope(),
      sheet: this.chosenSheet(),
      sheets: this.sheetList().map((s) => ({ id: s.id, name: s.name })),
      orientation: this.orientation(),
      paper: this.paper(),
      busy: this.busy,
    };
  }

  public uiSet(field: string, value: SheetsUiValue): string | null {
    // The selects are disabled while the PDF is made.
    if (this.busy) return UI_REASONS.pdfBusy;
    switch (field) {
      case 'scope':
        return setOneOf(field, value, ['workbook', 'sheet'], (v) => this.scope.set(v as PdfOptions['scope']));
      case 'sheet':
        return setOneOf(
          field,
          value,
          this.sheetList().map((s) => s.id),
          (v) => this.sheetId.set(v),
        );
      case 'orientation':
        return setOneOf(field, value, ['landscape', 'portrait'], (v) => this.orientation.set(v as PdfOptions['orientation']));
      case 'paper':
        return setOneOf(field, value, ['a4', 'letter'], (v) => this.paper.set(v as PdfOptions['paper']));
      default:
        return UI_REASONS.noField('pdf', field);
    }
  }

  /** "Export", disabled while the PDF is made and until the engine is ready. */
  public uiApply(): string | null {
    if (this.busy) return UI_REASONS.pdfBusy;
    if (!this.ready) return UI_REASONS.loading;
    this.emit();
    return null;
  }

  public uiCancel(): void {
    this.dismiss();
  }

  protected emit(): void {
    if (this.busy || !this.ready) return;
    const sheetId = this.scope() === 'sheet' ? (this.chosenSheet() ?? undefined) : undefined;
    this.confirm.emit({
      scope: this.scope(),
      ...(sheetId ? { sheetId } : {}),
      orientation: this.orientation(),
      paper: this.paper(),
    });
  }
}
